/* eslint-disable no-bitwise */
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { InverterData } from 'ngx-modbus';
import { cloneDeep } from 'lodash';
import { ApplyPipeModule } from 'ngx-zcs-components';
import { ProductModelService } from 'ngx-core-services';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';

const getLabels = () => ({
  notUse: 'not used',
  pv: 'photovoltaic',
  battery: 'battery',
  parallelMode: 'parallel mode',
  independentMode: 'independent mode',
  save: 'save',
  inputChannel: 'input channel',
});

export const V3InputTypeChannelConfig = {
  Input_Type: {
    start: 0x1011,
    length: 16,
  },
};

@Component({
  standalone: true,
  imports: [
    ApplyPipeModule,
    CommonModule,
    IonicModule,
    TranslateModule,
    MatSelectModule,
    MatButtonModule,
    MatFormFieldModule,
  ],
  selector: 'app-config-input-type-channel',
  templateUrl: './config-input-type-channel.component.html',
  styleUrls: ['./config-input-type-channel.component.scss'],
})
export class ConfigInputTypeChannelComponent {
  @Input() uiFramework: 'ionic' | 'material' = 'ionic';

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  inputTypeChannels: Array<{ input: number; id: number }> = [];
  availableInputMode = false;
  availablePvInputTypes: number[] = [];
  availableBatteryInputTypes: number[] = [];
  availableInputChannels: number[] = [];
  initialInputTypeChannels: Array<{ input: number; id: number }> = [];
  hasChanges = false;
  inputMode?: number;
  initialInputMode?: number;

  private _info: InverterData = {};
  @Input() set info(info: InverterData) {
    this._info = info;
    this.availableInputChannels = this.getAvailableInputTypes('AddressMask_Config_Basic1', 1, 17);
    this.inputTypeChannels = Array.from({ length: V3InputTypeChannelConfig.Input_Type.length }).map((_, i) => ({
      input: this._info[`INPUT_TYPE_CHANNEL_CONFIG${i}`] as number,
      id: i,
    })).filter(({ id }) => this.availableInputChannels.includes(id + 1));
    this.availablePvInputTypes = this.getAvailableInputTypes('AddressMask_Realtime_Input_PV1', 3);
    this.availableBatteryInputTypes = this.getAvailableInputTypes('AddressMask_Realtime_Input_Bat1', 7).map((id) => id + 127);
    this.availableInputMode = this.getAvailability('AddressMask_Config_Basic1', 1, 16);
    this.inputMode = this._info.PV_InputMode_Config as number;

    const product = new ProductModelService(this._info.SN as string);
    if (product.type?.name === 'inverter' && product.type.value === 'pv') {
      // filter input type channels by availablePvInputTypes
      this.inputTypeChannels = this.inputTypeChannels.filter(({ id }) => this.availablePvInputTypes.includes(id + 1));
    }

    this.initialInputTypeChannels = cloneDeep(this.inputTypeChannels);
    this.initialInputMode = this.inputMode;
    this.checkForChanges();
  }

  @Output() valueChange = new EventEmitter<string>();

/**
 * @param input 0 -> no used, [1, 127] -> PV #1-#127, [129, 256] -> Battery #1-#127
 */
  readonly getInputChannelName = (input: number) => {
    if (input === 0) {
      return this.labels.notUse;
    } else if (input <= 127) {
      return `${this.labels.pv} ${input}`;
    } else {
      return `${this.labels.battery} ${input - 127}`;
    }
  };

  private getAvailableInputTypes(maskKey: string, contentSize: number, skip = 4): number[] {
    const maskAsNumber: string | number = this._info[maskKey] as number;
    if (maskAsNumber == null) return [];
    const mask = maskAsNumber.toString(2).padStart(64, '0');

    return Array.from({ length: (64 - skip) / contentSize })
      .map((_, i) => mask.slice(-contentSize * (i + 1) - skip, -contentSize * i - skip)
      .split('')
      .reverse()
      .join(''))
      .map((available, i) => ({ available, id: i }))
      .filter(({ available }) => available !== '0'.repeat(contentSize))
      .map(({ id }) => id + 1);
  }

  private getAvailability(maskKey: string, contentSize: number, skip = 4): boolean {
    const maskAsNumber: string | number = this._info[maskKey] as number;
    if (maskAsNumber == null) return false;
    const mask = maskAsNumber.toString(2).padStart(64, '0');

    return Array.from({ length: (64 - skip) / contentSize })
      .map((_, i) => mask.slice(-contentSize * (i + 1) - skip, -contentSize * i - skip)
      .split('')
      .reverse()
      .join(''))
      .map((available) => ({ available }))
      .some(({ available }) => available !== '0'.repeat(contentSize));
  }

  onInputTypeChannelValueChange(index: number, event: CustomEvent) {
    const newValue = event.detail.value;
    if (this.inputTypeChannels[index]) {
      this.inputTypeChannels[index].input = newValue;
      this.checkForChanges();
    } else {
      throw new Error(`Input type channel ${index} not found, value=${newValue}`);
    }
  }

  onInputModeChange(event: CustomEvent) {
    this.inputMode = Number(`${event.detail.value}`);
    this.checkForChanges();
  }

  checkForChanges() {
    if (this.initialInputTypeChannels.length) {
      this.hasChanges = !this.inputTypeChannels.every((channel, index) =>
        channel.input === this.initialInputTypeChannels[index].input,
      );
    }

    if (this.availableInputMode) {
      this.hasChanges = this.hasChanges || this.inputMode !== this.initialInputMode;
    }
  }

  save() {
    if (this.availableInputMode && this.inputTypeChannels.length === 0) {
      this.valueChange.emit(JSON.stringify({
        /**
         * 0x1010
         * 1 register - 2 bytes
         * PV_InputMode_Config
         *
         * PV input Mode Selection  0: Parallel Mode 1:Independent mode
         */
        address: 0x1010,
        registers: [ 0, this.inputMode ? 1 : 0 ],
      }));
    } else if (this.inputTypeChannels.length > 0) {
      const inputChannels = this.inputTypeChannels
        .map(({ input, id }) => ({
          input,
          id,
          value: [ input >> 8, input & 0xff ],
        }));
      /**
       * 16 registers - 32 bytes
       */
      const registers = Array(0x10).fill(0).map((_, i) => {
        const channel = inputChannels.find(({ id }) => id === i);
        return channel ? channel.value : [0, 0];
      }).flat();

      this.valueChange.emit(JSON.stringify({
        /**
         * 0x1011 - 0x1020
         * 16 registers - 32 bytes
         * InputType_Channel0_Config - InputType_Channel15_Config
         */
        address: 0x1011,
        registers,
      }));
    }
  }
}
